import { useEffect, useRef, useState } from 'react';
import { Tag, Typography } from '@arco-design/web-react';
import { IconApps, IconCheck, IconRight, IconSettings } from '@arco-design/web-react/icon';
import type { PortalApplication } from '../model/portalModels';

const { Text } = Typography;

function ApplicationIcon({ application }: { application?: PortalApplication }) {
  if (!application) return <IconApps />;
  return <IconSettings />;
}

export function ApplicationSwitcher(props: {
  applications: PortalApplication[];
  activeApplication?: PortalApplication;
  disabled?: boolean;
  onSelect: (application: PortalApplication) => void;
}) {
  const [visible, setVisible] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const activeApplication = props.activeApplication;

  useEffect(() => {
    if (!visible) return undefined;
    const close = (event: PointerEvent) => {
      if (!switcherRef.current?.contains(event.target as Node)) setVisible(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [visible]);

  useEffect(() => {
    if (props.disabled) setVisible(false);
  }, [props.disabled]);

  return <div className="portal-system-switcher" ref={switcherRef}>
    <button className="portal-system-switcher__trigger" type="button" aria-expanded={visible}
      aria-label={`当前应用：${activeApplication?.appName ?? '暂无可用应用'}，点击切换应用`}
      disabled={props.disabled}
      onClick={() => setVisible(current => !current)}>
      <span className="portal-system-switcher__mark" aria-hidden="true">
        <ApplicationIcon application={activeApplication} />
      </span>
    </button>
    {visible ? <section className="portal-system-switcher__panel" aria-label="切换应用">
      <div className="portal-system-switcher__head">
        <div><strong>切换应用</strong><Text type="secondary">切换后注入对应一级导航</Text></div>
        <Tag color="arcoblue">{props.applications.length} 个可用</Tag>
      </div>
      <div className="portal-system-switcher__list">
        {props.applications.map(application => {
          const active = application.appCode === activeApplication?.appCode;
          return <button className={[
            'portal-system-switcher__item',
            active ? 'portal-system-switcher__item--active' : '',
          ].filter(Boolean).join(' ')} key={application.appCode} type="button"
          disabled={active || props.disabled} aria-current={active ? 'true' : undefined}
          onClick={() => {
            if (active) return;
            props.onSelect(application);
            setVisible(false);
          }}>
            <span className="portal-system-switcher__item-icon" aria-hidden="true">
              <ApplicationIcon application={application} />
            </span>
            <span className="portal-system-switcher__item-body">
              <span className="portal-system-switcher__item-title">
                <strong>{application.appName}</strong>
                <Tag color="green" size="small">已接入</Tag>
              </span>
              <span>{application.description ?? '由 Access 返回的授权应用'}</span>
            </span>
            {active ? <IconCheck className="portal-system-switcher__check" /> : <IconRight />}
          </button>;
        })}
      </div>
    </section> : null}
  </div>;
}
